import React, { useState, useEffect } from 'react';

// Mui
import { Box, Card, Grid, IconButton, Badge, Button } from '@mui/material';
import { Edit, Delete as DeleteIcon } from '@mui/icons-material';

import Swal from 'sweetalert2';

// Components
import CustomTable from '@components/Tables/CustomTable';
import FormGaleria from '@components/ppm/FormGaleria';
import Modal from "@components/ppm/ModalImages";

// Services
import ppmServices from '@services/PpmServices';

// Utils
import { convertBase64 } from '@utils/Utilities';
import moment from 'moment';

const TableGaleria = (props) => {
    const { setIsLoading, refresh } = props;
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [page] = useState(0);
    const [pageSize] = useState(10);
    const [openModal, setOpenModal] = useState(false);
    const [params, setParams] = useState({});
    const [openImages, setOpenImages] = useState(false);
    const [dataField, setDataField] = useState(false);

    const baseColumns = [
        { id: 'actividad', label: 'Actividad', align: 'left' },
        { id: 'fechaInicio', label: 'Fecha Evento', align: 'center' },
        { id: 'image', label: 'Galería', align: 'center', sort: false },
        { id: 'Accion', label: '', align: 'center' }
    ];

    const mapData = (data) =>
        data.map((item, index) => {
            return {
                Accion: (
                    <Box sx={{ display: 'flex', gap: 3 }}>
                        <IconButton onClick={() => handleEditClick(item)} sx={{ m: 0, p: 0 }} color="primaryDark">
                            <Edit />
                        </IconButton>
                        <IconButton onClick={() => handleDelete(item)} sx={{ m: 0, p: 0 }} color="error">
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                ),
                image: (
                    <Box sx={{ position: 'relative', display: 'inline-block', mt: .5 }}>
                        <Badge
                            badgeContent={item?.count ?? null}
                            color="error"
                            overlap="rectangular"
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        >
                            <Button
                                fullWidth
                                color={'primaryDark'}
                                variant={'outlined'}
                                onClick={() => {
                                    setOpenImages(true);
                                    setDataField(item);
                                }}
                                sx={{ whiteSpace: 'nowrap', width: '20vh' }}
                                disabled={item.count <= 0}
                            >
                                {item.count > 0 ? 'Ver imágenes' : 'Sin imágenes'}
                            </Button>
                        </Badge>
                    </Box>
                ),
                ...item,
            }
        });

    const ApiResponse = async () => {
        try {
            setLoading(true);
            const result = await ppmServices.getGalleryTable();
            const { results, response, message } = result;

            if (results) {
                const formatedData = response.data.map((item) => {
                    return {
                        ...item,
                        fechaInicio: moment(item.fechaInicio, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY'),
                    };
                })
                setData(mapData(formatedData));
            } else {
                throw new Error(message);
            }
        } catch (error) {
            Swal.fire({
                title: error.message,
                icon: 'warning',
                customClass: {
                    container: 'modal-alert',
                },
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        ApiResponse();
        //eslint-disable-next-line
    }, [refresh]);

    const handleEditClick = (value) => {
        setParams(value);
        setOpenModal(true);
    };

    const handleUpdateData = async (value) => {
        try {
            setIsLoading(true);

            // Filtrar y convertir los archivos según el tipo
            if (value.files && Array.isArray(value.files)) {
                for (let i = value.files.length - 1; i >= 0; i--) {
                    const fileObj = value.files[i];

                    // Si el archivo es base64 (data:image), lo mantenemos tal cual
                    if (fileObj.file.startsWith('data:image')) {
                        continue;
                    }

                    // Si es una URL, lo eliminamos
                    if (fileObj.file.startsWith('http')) {
                        value.files.splice(i, 1);
                        continue;
                    }

                    // Si el archivo es un Blob y no es una URL, lo convertimos a base64
                    const base64File = await convertBlobToBase64(fileObj.file);
                    value.files[i].file = base64File;
                }
            }

            const result = await ppmServices.updateEventGallery(value);
            const { results, message } = result;
            if (results) {
                Swal.fire({
                    title: message,
                    icon: 'success',
                    customClass: {
                        container: 'modal-alert',
                    },
                });

                setOpenModal(false);

                await ApiResponse();
            } else {
                throw new Error(message);
            }

        } catch (error) {
            Swal.fire({
                title: error.message,
                icon: 'warning',
                customClass: {
                    container: 'modal-alert',
                },
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Función para convertir un archivo Blob a base64
    const convertBlobToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const handleDelete = async (banner) => {
        const result = await Swal.fire({
            title: '¿Estás seguro de eliminarlo?',
            text: "Esta acción no se puede deshacer.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            reverseButtons: true,
        });

        if (result.isConfirmed) {
            try {
                setIsLoading(true);
                const deleteResult = await ppmServices.deleteEventGallery({ idEvento: banner?.idEvento });
                const { success, message } = deleteResult;

                if (success) {
                    Swal.fire({
                        title: message,
                        icon: 'success',
                        customClass: { container: 'modal-alert' },
                    });

                    await ApiResponse();
                } else {
                    throw new Error(message);
                }
            } catch (e) {
                Swal.fire({
                    title: e.message,
                    icon: 'warning',
                    customClass: { container: 'modal-alert' },
                });
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <Box component={Card} variant="outlined" borderRadius={2} padding={2} minHeight={100} elevation={0}>
            <Grid container>
                <Grid item xs={12}>
                    <CustomTable
                        rows={data}
                        columns={baseColumns}
                        total={data.length}
                        page={page}
                        pageSize={pageSize}
                        stickyHeader={true}
                        isLoading={loading}
                        maxHeight={600}
                        disableCardType
                        disableHover
                    />
                </Grid>
            </Grid>

            {openModal && (
                <FormGaleria
                    setModalOpen={setOpenModal}
                    modalOpen={openModal}
                    params={params}
                    setData={handleUpdateData}
                    showInut={false}
                />
            )}

            {openImages && (
                <Modal setOpenImages={setOpenImages} openImages={openImages} data={dataField} />
            )}
        </Box>
    );
};

export default TableGaleria;
